import React, { useEffect, useRef, useState, useCallback } from "react";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import "@arcgis/core/assets/esri/themes/light/main.css";
import { performSearch } from "../utils/searchUtil";
import axios from "axios";
import Papa from "papaparse";
import { fetchCensusTractsData, fetchZipcodeData, injectCensusDataIntoLayer } from "../utils/censusDataUtils";
import SearchComponent from "./SearchComponent";
import PolygonSelection from "./PolygonSelection";
import { 
  processAdoptionStatus, 
  createAdoptionStatusRenderer,
  createAdoptionStatusPopupTemplate 
} from "../utils/adoptionStatusUtils";

// CSV fetch functions using PapaParse
async function getSheetData() {
  // Poverty CSV remains unchanged
  const sheetUrl =
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vR2x0Lik-Flp1e9x-BL2Lrz-bt1lBnmfjyq-xaJre8U3nb-RmRRHHqZ5XyMczrfaA-UJDEmsMhBAJ_7/pub?output=csv";
  try {
    const response = await axios.get(sheetUrl);
    const parsed = Papa.parse(response.data, { 
      header: true, 
      skipEmptyLines: true,
      transformHeader: (header) => header.trim() 
    });
    
    console.log("Poverty data loaded:", parsed.data.length, "rows");
    console.log("CSV Headers:", parsed.meta.fields);
    
    // Check if we have the required columns
    const hasCensusTract = parsed.meta.fields.includes("Census Tract");
    const hasAdoptionStatus = parsed.meta.fields.includes("Adoption Status");
    
    console.log("Has Census Tract column:", hasCensusTract);
    console.log("Has Adoption Status column:", hasAdoptionStatus);
    
    return parsed.data;
  } catch (error) {
    console.error("Error fetching poverty CSV data:", error);
    return [];
  }
}

async function getChurchData() {
  // Updated Church CSV with ?output=csv link
  const churchCsvUrl =
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vQefnw4KOV5sBhvRHI-ptUNjDlBxWSE678iwq8mxuGGTbf2Odgc-w6x_H2UPs1g3cGf9DM9U4rdYNoA/pub?output=csv";
  try {
    const response = await axios.get(churchCsvUrl);
    const parsed = Papa.parse(response.data, { 
      header: true, 
      skipEmptyLines: true,
      transformHeader: (header) => header.trim() 
    });
    
    // Log the headers to verify we're getting the right columns
    console.log("Church data headers:", parsed.meta.fields);
    
    // Create a map of zip codes to arrays of church names for faster lookups
    const churchesByZip = {};
    parsed.data.forEach(church => {
      const zipCode = church["Zip Code"];
      const name = church["Name"];
      
      if (!zipCode || !name) return;
      
      // Convert any zip code to string and normalize it
      const zipStr = zipCode.toString().trim();
      
      if (!churchesByZip[zipStr]) {
        churchesByZip[zipStr] = [];
      }
      
      churchesByZip[zipStr].push(name);
    });
    
    console.log("Church data organized by ZIP code. Available ZIPs:", Object.keys(churchesByZip));
    
    // Return both the raw data and the lookup map
    return {
      rawData: parsed.data,
      churchesByZip: churchesByZip
    };
  } catch (error) {
    console.error("Error fetching Church CSV data:", error);
    return {
      rawData: [],
      churchesByZip: {}
    };
  }
}

const ArcGISMap = () => {
  const viewDivRef = useRef(null);
  const geoJsonLayerRef = useRef(null);

  // Map & view
  const [map, setMap] = useState(null);
  const [view, setView] = useState(null);
  const [layerLoaded, setLayerLoaded] = useState(false);

  // UI state
  const [selectedLayer, setSelectedLayer] = useState("censusTracts");
  const [searchField, setSearchField] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchStatus, setSearchStatus] = useState("");

  // Data from external sources
  const [sheetData, setSheetData] = useState({
    churchesByZip: {},
    adoptionStatusMap: {},
    censusTractData: {},
    zipCodeData: {},
  });

  // Fetch all external data on mount
  useEffect(() => {
    const apiKey = process.env.REACT_APP_CENSUS_API_KEY;

    Promise.all([
      getSheetData(),
      getChurchData(),
      apiKey ? fetchCensusTractsData(apiKey) : Promise.resolve({}),
      apiKey ? fetchZipcodeData(apiKey) : Promise.resolve({}),
    ]).then(([povertyRows, churchResult, tractData, zipData]) => {
      setSheetData({
        churchesByZip: churchResult.churchesByZip,
        adoptionStatusMap: processAdoptionStatus(povertyRows),
        censusTractData: tractData,
        zipCodeData: zipData,
      });
    }).catch(console.error);
  }, []);

  // Create the map and view on mount
  useEffect(() => {
    const newMap = new Map({ basemap: "topo-vector" });
    const mapView = new MapView({
      container: viewDivRef.current,
      map: newMap,
      zoom: 10,
      center: [-122.0081095, 37.5371513],
      popup: { 
        autoOpenEnabled: true,
        dockEnabled: false,
        dockOptions: {
          buttonEnabled: false,
          breakpoint: false,
          position: "top-right"
        }
      },
    });

    // Once the view is ready, set it up completely
    mapView.when(() => {
      console.log("Map view initialized");
      setMap(newMap);
      setView(mapView);
    });

    return () => {
      mapView?.destroy();
    };
  }, []);

  // Function to generate content for census tract popups - using useCallback to avoid dependency issues
  const generateCensusTractContent = useCallback((featureOrGraphic) => {
    const graphic = featureOrGraphic.graphic || featureOrGraphic;
    const attrs = graphic.attributes || {};
    
    // US Census API fields
    const totalPopulation = attrs.P1_001N || "N/A";
    const employmentRate = attrs.DP03_0004PE || "N/A";
    const totalHouseholds = attrs.DP02_0001E || "N/A";
    const medianHouseholdIncome = attrs.S1901_C01_012E || "N/A";
    
    // We'll add adoption status in the layer's popup template content function
    return `
      <div style="text-align: left;">
        <b>Census Tract:</b> ${attrs.NAMELSAD || "Unknown"}<br>
        <b>Total Population:</b> ${totalPopulation}<br>
        <b>Employment Rate:</b> ${employmentRate}%<br>
        <b>Total Households:</b> ${totalHouseholds}<br>
        <b>Median Household Income:</b> $${medianHouseholdIncome}<br>
        <b>Adoption Status:</b> <span id="adoption-status-placeholder"></span><br>
        <b>Adopted by:</b> <span id="adopted-by-placeholder"></span><br>
        <b>Churches:</b> <span id="churches-placeholder"></span><br>
        <b>Non-Profits:</b> <span id="nonprofits-placeholder"></span><br>
        <b>Local Business:</b> <span id="business-placeholder"></span>
      </div>
    `;
  }, []);

  // Function to generate content for zip code popups - using useCallback to avoid dependency issues
  const generateZipCodeContent = useCallback((feature) => {
    // Get all attributes from the feature
    const attrs = feature.graphic ? feature.graphic.attributes : feature.attributes || {};
    
    // Get attributes for the display
    const totalPopulation = attrs.P1_001N || "N/A";
    const employmentRate = attrs.DP03_0004PE || "N/A";
    const totalHouseholds = attrs.DP02_0001E || "N/A";
    const medianHouseholdIncome = attrs.S1901_C01_012E || "N/A";
    
    // Try different ways to get the ZIP code
    let zipCode = null;
    if (attrs.ZIP_CODE !== undefined && attrs.ZIP_CODE !== null) {
      zipCode = attrs.ZIP_CODE.toString().trim();
    } else if (attrs.ZIP !== undefined && attrs.ZIP !== null) {
      zipCode = attrs.ZIP.toString().trim();
    } else if (attrs.ZIPCODE !== undefined && attrs.ZIPCODE !== null) {
      zipCode = attrs.ZIPCODE.toString().trim();
    } else {
      // Try to find any attribute that looks like a ZIP code
      for (const key in attrs) {
        if (key.toUpperCase().includes('ZIP') && attrs[key] !== null) {
          zipCode = attrs[key].toString().trim();
          console.log(`Found ZIP in alternate field: ${key}=${zipCode}`);
          break;
        }
      }
    }
    
    // Get church names for this ZIP from our precomputed lookup
    let churchNames = "No churches found";
    if (zipCode && sheetData.churchesByZip[zipCode] && sheetData.churchesByZip[zipCode].length > 0) {
      churchNames = sheetData.churchesByZip[zipCode].join(", ");
      console.log(`Found ${sheetData.churchesByZip[zipCode].length} churches for ZIP ${zipCode}`);
    } else {
      console.log(`No churches found for ZIP ${zipCode}`);
    }

    return `
      <div style="text-align: left;">
        <h3>ZIP Code: ${zipCode || "N/A"}</h3>
        <b>Total Population:</b> ${totalPopulation}<br>
        <b>Employment Rate:</b> ${employmentRate}%<br>
        <b>Total Households:</b> ${totalHouseholds}<br>
        <b>Median Household Income:</b> $${medianHouseholdIncome}<br>
        <b>Adoption Status:</b> <span id="adoption-status-placeholder"></span><br>
        <b>Churches:</b> ${churchNames}
      </div>
    `;
  }, [sheetData.churchesByZip]);

  // Load the GeoJSON layer and define the popup template based on selectedLayer
  useEffect(() => {
    if (!map || !view) return;
    map.removeAll();

    // This effect re-runs whenever the layer choice or the fetched data changes.
    // Until the new layer is ready, no layer should be considered searchable, and a
    // layer instance from a superseded run must never claim the ref — otherwise a
    // search can run against the previous layer (wrong fields, stale popup data).
    let cancelled = false;
    geoJsonLayerRef.current = null;
    setLayerLoaded(false);

    // Determine GeoJSON URL based on selected layer
    const geojsonUrl =
      selectedLayer === "censusTracts"
        ? `${process.env.PUBLIC_URL}/bay_area_tracts_geometry.geojson`
        : `${process.env.PUBLIC_URL}/BayAreaZipCodes.geojson`;

    // Define popup template based on layer type
    const popupTemplate =
      selectedLayer === "censusTracts"
        ? {
            title: "{NAMELSAD}", // This displays the census tract identifier
            content: generateCensusTractContent
          }
        : {
            title: "Zip Code: {ZIP_CODE}",
            content: generateZipCodeContent
          };

    // Create the GeoJSON layer with a basic renderer
    // Initially use a simple renderer, we'll update it after the layer loads
    const geoJsonLayer = new GeoJSONLayer({
      url: geojsonUrl,
      outFields: ["*"],
      renderer: {
        type: "simple",
        symbol: {
          type: "simple-fill",
          color: "rgba(128, 128, 128, 0.3)", // Gray for default
          outline: {
            color: "black",
            width: 1,
          },
        }
      },
      popupTemplate,
    });

    // When layer loads, enrich it with census data and handle the view
    geoJsonLayer.when(() => {
      if (cancelled) return;
      geoJsonLayerRef.current = geoJsonLayer;

      try {
        // Apply adoption status renderer independently of census data
        if (selectedLayer === "censusTracts" && Object.keys(sheetData.adoptionStatusMap).length > 0) {
          geoJsonLayer.renderer = createAdoptionStatusRenderer(sheetData.adoptionStatusMap);
          geoJsonLayer.popupTemplate = createAdoptionStatusPopupTemplate(
            geoJsonLayer.popupTemplate,
            sheetData.adoptionStatusMap
          );
        }

        // Enrich with census demographic data if available
        if (selectedLayer === "censusTracts" && Object.keys(sheetData.censusTractData).length > 0) {
          injectCensusDataIntoLayer(geoJsonLayer, sheetData.censusTractData, false).catch(console.error);
        } else if (selectedLayer === "zipCodes" && Object.keys(sheetData.zipCodeData).length > 0) {
          injectCensusDataIntoLayer(geoJsonLayer, sheetData.zipCodeData, true).catch(console.error);
        }
      } catch (error) {
        console.error("Error processing layer:", error);
      }
      
      setLayerLoaded(true);
      view.goTo(geoJsonLayer.fullExtent).catch(console.warn);
    })
    .catch((error) => console.error("Error loading GeoJSON layer:", error));

    map.add(geoJsonLayer);

    return () => {
      cancelled = true;
    };
  }, [selectedLayer, map, view, generateCensusTractContent, generateZipCodeContent, sheetData]);

  // Handle search functionality using performSearch utility
  const handleSearch = async (e) => {
    e.preventDefault();
    setSearchStatus("");
    
    if (!geoJsonLayerRef.current) {
      setSearchStatus("Layer is not ready. Please wait and try again.");
      return;
    }
    
    if (!layerLoaded) {
      setSearchStatus("Layer is still loading. Please wait and try again.");
      return;
    }
    
    // Validate search inputs
    if (!searchField) {
      setSearchStatus("Please select a search field.");
      return;
    }
    
    if (!searchValue.trim()) {
      setSearchStatus("Please enter a search value.");
      return;
    }
    
    console.log("Starting search with field:", searchField, "value:", searchValue);
    
    // Perform the search with current popup template functions
    const result = await performSearch({
      view,
      geoJsonLayer: geoJsonLayerRef.current,
      searchField,
      searchValue,
      setSearchValue, // Pass the setter function to allow clearing the input
    });
    
    if (!result.success) {
      console.error("Search error:", result.message);
      setSearchStatus(result.message);
    } else {
      console.log("Search completed successfully.");
      // Clear any error status
      setSearchStatus("");
    }
  };

  // Toggle between census tract view and zip code view
  const toggleLayer = () => {
    setSelectedLayer((prev) =>
      prev === "censusTracts" ? "zipCodes" : "censusTracts"
    );
    // Reset search fields when toggling layers
    setSearchField("");
    setSearchValue("");
    setSearchStatus("");
  };

  return (
    <div style={{ position: "relative", height: "100vh", width: "100vw" }}>
      <div ref={viewDivRef} style={{ height: "100%", width: "100%" }}></div>
      
      {/* Search Component with Toggle Button */}
      <SearchComponent
        searchField={searchField}
        setSearchField={setSearchField}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onSearch={handleSearch}
        selectedLayer={selectedLayer}
        searchStatus={searchStatus}
        toggleLayer={toggleLayer}
      />
      
      {/* Polygon Selection Component */}
      {layerLoaded && geoJsonLayerRef.current && (
        <PolygonSelection 
          map={map} 
          view={view} 
          geoJsonLayer={geoJsonLayerRef.current} 
        />
      )}
    </div>
  );
};

export default ArcGISMap;